import moment from 'moment';
import Cookies from 'universal-cookie';
import DOMPurify from 'isomorphic-dompurify';

/**
 * Validates that a refId parameter is safe to use in URLs
 * Prevents open redirect vulnerabilities by allowing only alphanumeric, hyphens, and underscores
 * @param {string} refId - The reference ID to validate
 * @returns {string|null} - The validated refId or null if invalid
 */
export const validateRefId = (refId) => {
  if (!refId || typeof refId !== 'string') {
    return null;
  }
  
  // Allow only alphanumeric characters, hyphens, and underscores (typical for UUIDs and safe IDs)
  // Max length of 200 characters to prevent abuse
  const safeRefIdPattern = /^[a-zA-Z0-9_-]{1,200}$/;
  
  if (safeRefIdPattern.test(refId)) {
    return refId;
  }
  
  return null;
};

export const stringPurifer = (str) => {
  if (!!str) {
    const htmlEntities = {
      "&lt;": "<",
      "&gt;": ">",
    };
    if (
      DOMPurify.sanitize(
        str.replace(/&[\w#]+;/g, (entity) => {
          return htmlEntities[entity] || entity;
        })
      ) !== str
    ) {
      throw Error("field_validiation_error_message_not_accepted");
    }
    return str;
  }
  return "";
};

export const searchTaskFilter = (response, searchTerm) => {
  const lowerCaseSearchTerm = searchTerm.toLowerCase();

  return response.reduce((acc, item) => {
    if (item.taskType === null && Array.isArray(item.candidateTaskList)) {
      //Group
      const filteredList = item.candidateTaskList.filter((task) =>
        task.taskName.toLowerCase().includes(lowerCaseSearchTerm)
      );

      if (filteredList.length > 0) {
        acc.push({
          ...item,
          candidateTaskList: filteredList,
        });
      }
    } else if (item.taskType !== null && item.candidateTaskList === null) {
      //Parent task
      if (item.taskName.toLowerCase().includes(lowerCaseSearchTerm)) {
        acc.push(item);
      }
    }

    return acc;
  }, []);
}

export const isValidUrl = urlString=> {
    var urlPattern = new RegExp('^(https?:\\/\\/)?'+ // validate protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // validate domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))'+ // validate OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // validate port and path
  '(\\?[;&a-z\\d%_.~+=-]*)?'+ // validate query string
  '(\\#[-a-z\\d_]*)?$','i'); // validate fragment locator
return !!urlPattern.test(urlString);
}

/**
 * Securely validates a URL to prevent Open Redirect vulnerabilities (CWE-601)
 * Ensures the URL uses safe protocols (http/https only) and has valid format
 * @param {string} url - The URL to validate
 * @param {Function} dispatch - Optional Redux dispatch function for error handling
 * @param {Function} t - Optional translation function for error messages
 * @returns {string|false} - Returns the validated URL string if safe, false if invalid
 */
export const isSecureUrl = (url, dispatch, t) => {
  if (!url || typeof url !== 'string') {
    if (dispatch && t) {
      dispatch({
        type: 'SNACK_OPEN',
        payload: {
          msg: t('invalid_link_error'),
          type: 'error'
        }
      });
    }
    return false;
  }
  
  try {
    const urlObj = new URL(url);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      if (dispatch && t) {
        dispatch({
          type: 'SNACK_OPEN',
          payload: {
            msg: t('invalid_link_error'),
            type: 'error'
          }
        });
      }
      return false;
    }
    
    // Additional validation: ensure hostname exists
    if (!urlObj.hostname) {
      if (dispatch && t) {
        dispatch({
          type: 'SNACK_OPEN',
          payload: {
            msg: t('invalid_link_error'),
            type: 'error'
          }
        });
      }
      return false;
    }
    
    return url;
  } catch (error) {
    // Invalid URL format
    if (dispatch && t) {
      dispatch({
        type: 'SNACK_OPEN',
        payload: {
          msg: t('invalid_link_error'),
          type: 'error'
        }
      });
    }
    return false;
  }
}

export const getToken = () => {
    const cookies = new Cookies();

    return cookies.get('token');
}

export const getEventColor = (evStatus) => {
    const expArr = ['expired', 'finished'];

    if(evStatus.toLowerCase() === 'active') {
        return 'cm-active-status';
    } else if(expArr.includes(evStatus.toLowerCase())) {
        return 'cm-expired-status';
    } else {
        return 'cm-yet-to-start-status';
    }
}

//Add leading zero to single digit number
export const addPad = (num) => {
  return num === 0 ? '0' : String(num).padStart(2, '0')
};

// this gives an object with dates as keys
export const dateWiseGroup = (data) => {
    const groups = data.reduce((groups, task) => {
        //const date = new Date(task.taskStartDate).toISOString().split('T')[0];
        const date = moment(task.taskStartDate).format('dddd / DD/MM/YYYY');
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(task);

        return groups;
      }, {});
      
      // Edit: to add it in the array format instead
      const groupArrays = Object.keys(groups).map((date) => {
        return {
          title: date,
          data: groups[date]
        };
      });
      
    return groupArrays;
}

export const getInitials = (str) => {
    if(!str) return '';

    // eslint-disable-next-line
    return str.split(' ').map((element,index,array)=> {
        if(index === 0 || index===array.length-1 ){   
            return element[0]
        }
    }).join('')
}

export const checkPassword = (str) => {
    var re = /^(?=.*\d)(?=.*[@#$%^&+=])(?=.*[a-z])(?=.*[A-Z]).{8,100}$/;
    // var re = /^(?=.*\d)(?=.*[!@#$%^&*)(-_+=:;'"`~><?\|{}.,\]\[])(?=.*[a-z])(?=.*[A-Z]).{8,20}$/;
    return re.test(str);
}

export const inputValidation = (
  str,
  required,
  wordLimit,
  requiredmessage,
  wordLimitmessage,
  regexType,
  regexmessage
) => {
  const regexBucket = {
    regexA: /^[\p{L}\p{M}0-9.:'ー\u00B7\-]+([ .\-'ー\u00B7][\p{L}\p{M}0-9.:'ー\u00B7\-]+)*$/u, // FIRST NAME AND LAST NAME - supports all languages (Latin, Arabic, Devanagari/Hindi, Cyrillic, Greek, CJK/Mandarin/Japanese, Hangul/Korean, Thai, Urdu, Vietnamese, etc.)
    regexB: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/, // FOR EMAIL
    regexC: /^(?=.*\d)(?=.*[@#$%^&+=])(?=.*[a-z])(?=.*[A-Z]).{8,20}$/ // PASSWORD
  };
  let finalString = null;
  [required, wordLimit, regexType].forEach((el, ind) => {
    if (!el || !!finalString) {
      return;
    }
    switch (ind) {
      case 0:
        if (str?.trim() === "") {
          finalString = requiredmessage;
        }
        break;
      case 1:
        if (!!str && str?.length > wordLimit) {
          finalString = wordLimitmessage;
        }
        break;
      case 2:
        const finalRegex = regexBucket[regexType];
        if (!!str && finalRegex && !finalRegex.test(str)) {
          finalString = regexmessage;
        }
        break;
      default:
    }
  });
  return finalString
};

export const replaceTranslationStringWithLink = (inputString, data) =>  {
    // Regular expression to match placeholders like {{placeholder}}
    const placeholderRegex = /{{\w+}}/g;
  
    // Replace each placeholder with an anchor tag based on the order of objects in the array
    let index = 0;
    const replacedString = inputString.replace(placeholderRegex, () => {
      const replacementObject = data[index++];
      if (replacementObject) {
        const { value, link } = replacementObject;
        return `<a href="${link}" target="_blank" class="text-decoration-underline">${value}</a>`;
      } else {
        return ''; // No matching object, replace with an empty string
      }
    });
  
    return replacedString;
}

export const roundOffMins = (momentVal, timezone) => {
  // const start = moment(momentVal, 'DD/MM/YYYY');
   const start = moment(momentVal, 'DD/MM/YYYY HH:mm');

   const remainder = 15 - (start.minute() % 15);
   return start.add(remainder, "minutes");
}

export const replaceDynamicString = (inputString, replacementArray,highlight=false) => {
  const pattern = /\{\{.*?\}\}/g; // Regular expression pattern to match "{{...}}"
  let replacementIndex = 0;

  if(highlight){
     let  finalObj = []
       
      finalObj =inputString.replace(pattern, () => {
          const replacement = replacementArray[replacementIndex];
          replacementIndex++;
          return `<strong> ${replacement} </strong>`;
        })
      return finalObj
  }
  return inputString.replace(pattern, () => {
    const replacement = replacementArray[replacementIndex];
    replacementIndex++;
    return replacement;
  });
}

export const segregateTasks = (candidateAllTaskList) => {
  const candidateTaskList = [];
  const candidateCompletedTaskList = [];
  const count = { notCompletedTabCount: 0, completedTabCount: 0, completedTasks: 0 }

  candidateAllTaskList.forEach(el => {
    if(el.taskType) {
      el.taskName = el.name;
      el.taskId = el.id;
    }
    if(el.startDate) el.taskStartDate = el.startDate;
    if(el.endDate) el.taskEndDate = el.endDate;

    //Group
    let yetToStartList = null;
    let completedList = null;
    let currentData = JSON.parse(JSON.stringify(el));
    if(el.taskType === null && el.candidateTaskList?.length > 0) {
        yetToStartList = el.candidateTaskList.filter(el => el.taskTab === "YET_TO_START");
        completedList = el.candidateTaskList.filter(el => el.taskTab === "COMPLETED");

        if(yetToStartList?.length > 0) {
            candidateTaskList.push({ ...currentData, candidateTaskList: yetToStartList })
            count.notCompletedTabCount = count.notCompletedTabCount + yetToStartList?.length;
        }

        if(completedList?.length > 0) {
            candidateCompletedTaskList.push({ ...currentData, candidateTaskList: completedList })
            count.completedTabCount = count.completedTabCount + completedList?.length;

            completedList.forEach(innerEl => {
              if(innerEl.taskStatus === "COMPLETED") count.completedTasks = count.completedTasks + 1;
            })
        }
    } else {
        //Task
        if(el.taskTab !== null) {
            if(el.taskTab === "YET_TO_START") {
              candidateTaskList.push({ ...currentData});
              count.notCompletedTabCount = count.notCompletedTabCount + 1;
            }
            if(el.taskTab === "COMPLETED") {
              candidateCompletedTaskList.push({ ...currentData});
              count.completedTabCount = count.completedTabCount + 1;
              if(el.taskStatus === "COMPLETED") count.completedTasks = count.completedTasks + 1;
            }
        }
    }
  });

  return { candidateTaskList, candidateCompletedTaskList, count };
}

export const sanitiseInput = (inputTxt) => decodeURIComponent(encodeURIComponent(inputTxt))

export const hasMultipleHtmlTags = (str) => {
  const tags = str.match(/<\s*([a-zA-Z0-9]+)(\s+[^>]*)?>/g);
  return tags && tags.length > 0;
}